export default {
  id: "compliance",
  title: "Compliance Dashboard & Signing Portal",
  section: "People",
  order: 82,
  keywords: "compliance forms signing portal client documents ADP import regenerate expiring signature guardian",
  body: `
## What it is

Two compliance systems live in the app:

- **Client form compliance** — consent and program forms that clients or
  their guardians must sign, tracked on the Compliance page and signed
  through an emailed link. This guide covers that.
- **Caregiver document compliance** — licenses, CPR, certifications — lives
  on each caregiver's Compliance tab and the dashboard queue (see the
  Caregivers guide).

## How the dashboard works

The Compliance page is a **client-by-form matrix**: one row per client, one
column per form, a colored status dot per cell:

- **Signed** / **In-Person** — done (in-person means signed on paper and
  recorded).
- **Expiring** / **Expired** — done but aging out; re-send before Expired.
- **Partially Signed** — some required signers finished, others haven't
  (e.g. client signed, guardian hasn't).
- **Pending** — sent, not yet signed.
- **Missing** — never sent.
- **N/A** — the form doesn't apply to that client's configuration.

Search and the Office filter narrow the matrix; the legend at the top
decodes the dots.

## Step by step: getting forms signed

1. Find the client's row; look for Missing/Expired/Pending dots.
2. Click the **Send (paper-plane)** action on the client.
3. Pick which forms to send and the recipient. The recipient gets an email
   with a **tokenized signing link** — the link itself is the key, no
   account or login needed.
4. The recipient opens the link and sees a simple portal: the client's
   forms, a progress bar, and Sign buttons.
5. Each form walks them through its questions — yes/no, initials,
   checkboxes, text; later sections can appear or disappear based on
   earlier answers — then asks for their **full legal name** and a
   **drawn signature**.
6. As forms come back, the matrix dots update automatically; partially
   complete stays Partially Signed until every required signer is done.
7. Signed forms are stored as PDFs — the portal (and the client's
   documents) show **View PDF**.

Guardians get guardian-labeled fields via their own link, so a client and
guardian can sign independently.

## Other actions

- **Import from ADP** — pull compliance data across from an ADP export
  instead of collecting signatures again.
- **Regenerate Missing PDFs** — if any signed form's PDF file is missing,
  this rebuilds them in bulk and reports success/failure counts.
`,
};
